/**
 * Scenario Configuration
 *
 * Centralized configuration for all target system + auth method combinations.
 * Used by both form rendering and payload transformation.
 *
 * @module AuthenticationFields/scenarioConfig
 */

// ============================================================================
// Scenario Configurations
// ============================================================================

/**
 * Configuration for each scenario including:
 * - targetSystemId: UUID for the target system (backend identifier)
 * - backendType: The connection type for the API
 * - authScheme: The auth scheme for the API
 * - fieldMapping: Maps frontend field names to backend field names
 */
const SCENARIO_CONFIG = {
  // -------------------------------------------------------------------------
  // Amazon Bedrock
  // -------------------------------------------------------------------------
  'amazon-bedrock:access-key': {
    targetSystemId: 'b5e33248-1145-465b-935f-20ac88e15fab',
    backendType: 'bedrock',
    authScheme: 'apiKey',
    fieldMapping: {
      accessKeyId: 'keyId',
      secretAccessKey: 'keySecret',
      awsRegion: 'region'
    }
  },

  // -------------------------------------------------------------------------
  // Google Vertex AI - Service Account
  // -------------------------------------------------------------------------
  'vertex-ai:service-account': {
    targetSystemId: '44a30473-605d-4ab4-b693-ac7fdaabd184',
    backendType: 'vertex',
    authScheme: 'Bearer',
    fieldMapping: {
      serviceAccountEmail: 'serviceAccountEmail',
      projectId: 'projectId',
      privateKey: 'privateKey',
      location: 'location'
    }
  },

  // -------------------------------------------------------------------------
  // Microsoft Azure Copilot - OAuth 2 Client Credentials
  // -------------------------------------------------------------------------
  'azure-openai:oauth': {
    targetSystemId: 'ebca1e8f-aeaa-4753-9fcb-18efd9060866',
    backendType: 'mscopilot',
    authScheme: 'oauth2-client-credentials',
    fieldMapping: {
      tenantId: 'tenantId',
      clientId: 'clientId',
      clientSecret: 'clientSecret',
      scope: 'scope'
    }
  }
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Builds scenario key from target system and auth method
 *
 * @param {string} targetSystem - Target system identifier
 * @param {string} authMethod - Auth method identifier
 * @returns {string} Scenario key
 */
export const buildScenarioKey = (targetSystem, authMethod) =>
  `${targetSystem}:${authMethod}`;

/**
 * Gets scenario configuration
 *
 * @param {string} targetSystem - Target system identifier
 * @param {string} authMethod - Auth method identifier
 * @returns {Object|null} Scenario config or null
 */
export const getScenarioConfig = (targetSystem, authMethod) => {
  const key = buildScenarioKey(targetSystem, authMethod);

  return SCENARIO_CONFIG[key] || null;
};

/**
 * Gets backend connection type for a scenario
 *
 * @param {string} targetSystem - Target system identifier
 * @param {string} authMethod - Auth method identifier
 * @returns {string} Backend connection type
 */
export const getBackendType = (targetSystem, authMethod) => {
  const config = getScenarioConfig(targetSystem, authMethod);

  return config?.backendType || 'bedrock';
};

/**
 * Gets auth scheme for a scenario
 *
 * @param {string} targetSystem - Target system identifier
 * @param {string} authMethod - Auth method identifier
 * @returns {string} Auth scheme
 */
export const getAuthScheme = (targetSystem, authMethod) => {
  const config = getScenarioConfig(targetSystem, authMethod);

  return config?.authScheme || 'apiKey';
};

/**
 * Gets target system ID (UUID) for a scenario
 *
 * @param {string} targetSystem - Target system identifier
 * @param {string} authMethod - Auth method identifier
 * @returns {string} Target system UUID
 */
export const getTargetSystemId = (targetSystem, authMethod) => {
  const config = getScenarioConfig(targetSystem, authMethod);

  return config?.targetSystemId || '';
};

/**
 * Transforms form parameters to backend auth parameters
 *
 * @param {string} targetSystem - Target system identifier
 * @param {string} authMethod - Auth method identifier
 * @param {Object} formParams - Form parameters object
 * @returns {Object} Backend auth parameters
 *
 * Note: Preserves null values (used in edit mode for unchanged sensitive fields)
 */
export const transformAuthParams = (targetSystem, authMethod, formParams) => {
  const config = getScenarioConfig(targetSystem, authMethod);

  if (!config) {
    return {};
  }

  const { fieldMapping } = config;
  const result = {};

  Object.entries(fieldMapping).forEach(([frontendKey, backendKey]) => {
    const value = formParams[frontendKey];

    // Preserve null values (for sensitive fields in edit mode)
    // Only use empty string for undefined
    result[backendKey] = value !== undefined ? value : '';
  });

  return result;
};

/**
 * Gets all registered scenario keys
 *
 * @returns {string[]} Array of scenario keys
 */
export const getRegisteredScenarios = () => Object.keys(SCENARIO_CONFIG);

/**
 * Checks if a scenario is registered
 *
 * @param {string} targetSystem - Target system identifier
 * @param {string} authMethod - Auth method identifier
 * @returns {boolean} True if scenario exists
 */
export const isValidScenario = (targetSystem, authMethod) => {
  const key = buildScenarioKey(targetSystem, authMethod);

  return Boolean(SCENARIO_CONFIG[key]);
};

// ============================================================================
// Exports
// ============================================================================

export { SCENARIO_CONFIG };

export default SCENARIO_CONFIG;
